import { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Sidebar from "../../Components/Sidebar/Sidebar";
import Header from "../../Components/Header/Header";
import classes from "./ConsultationType.module.css";
import { BASE_URL } from "../../Components/API/Api";

import {
    FaStethoscope,
    FaPlus,
    FaEdit,
    FaTrash,
    FaSearch,
    FaRedoAlt,
    FaSpinner,
    FaExclamationTriangle,
    FaTimes,
    FaSave,
} from "react-icons/fa";

// =========================================================
// MONEY FORMATTING HELPERS
// =========================================================
const formatMoney = (raw) => {
    if (raw === "" || raw === null || raw === undefined) return "";
    const str = String(raw).replace(/,/g, "");
    if (str === "" || str === ".") return str;

    const [intPart, decPart] = str.split(".");
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    if (decPart !== undefined) {
        return `${formattedInt}.${decPart}`;
    }
    return formattedInt;
};

const unformatMoney = (formatted) => {
    if (formatted === "" || formatted === null || formatted === undefined) {
        return "";
    }
    return String(formatted).replace(/,/g, "");
};

// =========================================================
// CREATE MODAL
// =========================================================
const CreateConsultationTypeModal = ({ isOpen, onClose, onSaved }) => {
    const [form, setForm] = useState({
        name: "",
        consultation_fee: "",
        service_charge: "",
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setForm({ name: "", consultation_fee: "", service_charge: "" });
            setErrors({});
            setIsSubmitting(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Apply comma formatting to money fields
        const moneyFields = ["consultation_fee", "service_charge"];
        const nextValue = moneyFields.includes(name)
            ? formatMoney(value)
            : value;

        setForm((prev) => ({ ...prev, [name]: nextValue }));

        if (errors[name]) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
    };

    const validate = () => {
        const errs = {};

        if (!form.name.trim()) errs.name = "Name is required";
        else if (form.name.length > 1000)
            errs.name = "Name must be 1000 characters or less";

        // Consultation fee — strip commas before parsing
        const cleanFee = unformatMoney(form.consultation_fee);
        if (cleanFee === "" || cleanFee === null) {
            errs.consultation_fee = "Consultation fee is required";
        } else {
            const fee = parseFloat(cleanFee);
            if (isNaN(fee) || fee < 0.01)
                errs.consultation_fee = "Must be at least 0.01";
        }

        // Service charge — optional
        const cleanCharge = unformatMoney(form.service_charge);
        if (cleanCharge !== "" && cleanCharge !== null) {
            const charge = parseFloat(cleanCharge);
            if (isNaN(charge) || charge < 0.01)
                errs.service_charge = "Must be at least 0.01";
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setIsSubmitting(true);
        try {
            const companyId = parseInt(localStorage.getItem("companyId") || "1");

            const cleanFee = unformatMoney(form.consultation_fee);
            const cleanCharge = unformatMoney(form.service_charge);

            const payload = {
                company_id: companyId,
                name: form.name.trim(),
                consultation_fee: parseFloat(cleanFee),
                service_charge:
                    cleanCharge !== "" ? parseFloat(cleanCharge) : null,
            };

            const response = await axios.post(
                `${BASE_URL}/api/consultation_type/create`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            Swal.fire({
                icon: "success",
                title: "Success!",
                text: "Consultation type created successfully!",
                timer: 1500,
                showConfirmButton: false,
                timerProgressBar: true,
            });

            if (onSaved) onSaved(response.data?.data || payload);
            onClose();
        } catch (error) {
            let msg = "Failed to create consultation type.";
            if (error.response?.data?.message) {
                const message = error.response.data.message;
                if (typeof message === "object") {
                    const firstKey = Object.keys(message)[0];
                    msg = Array.isArray(message[firstKey])
                        ? message[firstKey][0]
                        : JSON.stringify(message);
                } else if (typeof message === "string") {
                    msg = message;
                }
            } else if (error.response?.data?.errors) {
                const errs = error.response.data.errors;
                const firstKey = Object.keys(errs)[0];
                if (firstKey && errs[firstKey][0]) msg = errs[firstKey][0];
            } else if (error.request) {
                msg = "Unable to connect to server.";
            }
            Swal.fire({
                icon: "error",
                title: "Error!",
                text: msg,
                confirmButtonColor: "#2F80ED",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={classes.modalOverlay} onClick={onClose}>
            <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
                <div className={classes.modalHeader}>
                    <div className={classes.modalHeaderLeft}>
                        <div className={classes.modalHeaderIcon}>
                            <FaStethoscope />
                        </div>
                        <div>
                            <h3>Create Consultation Type</h3>
                            <p>Add a new consultation type for billing</p>
                        </div>
                    </div>
                    <button
                        className={classes.modalCloseBtn}
                        onClick={onClose}
                        type="button"
                        disabled={isSubmitting}
                    >
                        <FaTimes />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className={classes.modalBody}>
                        <div className={classes.field}>
                            <label>
                                Consultation Name <span className={classes.required}>*</span>
                            </label>
                            <input
                                type="text"
                                name="name"
                                placeholder="e.g. General Consultation"
                                value={form.name}
                                onChange={handleChange}
                                className={errors.name ? classes.inputError : ""}
                                disabled={isSubmitting}
                                maxLength={1000}
                            />
                            {errors.name && (
                                <span className={classes.fieldError}>{errors.name}</span>
                            )}
                        </div>

                        <div className={classes.row}>
                            <div className={classes.field}>
                                <label>
                                    Consultation Fee <span className={classes.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    name="consultation_fee"
                                    placeholder="0.00"
                                    value={form.consultation_fee}
                                    onChange={handleChange}
                                    className={
                                        errors.consultation_fee ? classes.inputError : ""
                                    }
                                    disabled={isSubmitting}
                                />
                                {errors.consultation_fee ? (
                                    <span className={classes.fieldError}>
                                        {errors.consultation_fee}
                                    </span>
                                ) : (
                                    <span className={classes.fieldHint}>
                                        Minimum 0.01
                                    </span>
                                )}
                            </div>

                            <div className={classes.field}>
                                <label>Service Charge</label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    name="service_charge"
                                    placeholder="0.00"
                                    value={form.service_charge}
                                    onChange={handleChange}
                                    className={
                                        errors.service_charge ? classes.inputError : ""
                                    }
                                    disabled={isSubmitting}
                                />
                                {errors.service_charge ? (
                                    <span className={classes.fieldError}>
                                        {errors.service_charge}
                                    </span>
                                ) : (
                                    <span className={classes.fieldHint}>
                                        Optional — min 0.01
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={classes.modalFooter}>
                        <button
                            type="button"
                            className={classes.cancelBtn}
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={classes.saveBtn}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <FaSpinner className={classes.spin} /> Creating...
                                </>
                            ) : (
                                <>
                                    <FaSave /> Create Consultation Type
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// =========================================================
// MAIN PAGE
// =========================================================
const ConsultationType = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");

    // Create
    const [isCreateOpen, setIsCreateOpen] = useState(false);

    // Edit
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [editError, setEditError] = useState("");
    const [editingType, setEditingType] = useState(null);

    // Delete
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // ---------------------------------------------------------------
    // FETCH  →  GET /api/consultation_type
    // ---------------------------------------------------------------
    const fetchTypes = async () => {
        setLoading(true);
        setError("");
        try {
            const companyId = parseInt(localStorage.getItem("companyId") || "1");
            const response = await axios.get(
                `${BASE_URL}/api/consultation_type`,
                {
                    params: { company_id: companyId, per_page: 100 },
                    headers: {
                        Accept: "application/json",
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            console.log("Consultation types response:", response.data);

            let list = [];
            if (response.data?.data) {
                if (Array.isArray(response.data.data)) list = response.data.data;
                else if (response.data.data.data) list = response.data.data.data;
                else if (response.data.data.consultation_types)
                    list = response.data.data.consultation_types;
            } else if (Array.isArray(response.data)) {
                list = response.data;
            }

            setTypes(list);
        } catch (err) {
            console.error("Error fetching consultation types:", err);
            let msg = "Failed to load consultation types.";
            if (err.response?.data?.message) {
                const m = err.response.data.message;
                msg = typeof m === "string" ? m : "Failed to load.";
            } else if (err.request) {
                msg = "Unable to connect to server.";
            }
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTypes();
    }, []);

    // ---------------------------------------------------------------
    // FILTER
    // ---------------------------------------------------------------
    const filteredTypes = types.filter((t) => {
        const q = search.toLowerCase();
        const name = String(t.name || "").toLowerCase();
        return !q || name.includes(q);
    });

    // ---------------------------------------------------------------
    // CREATE
    // ---------------------------------------------------------------
    const handleCreateSaved = () => {
        fetchTypes();
    };

    // ---------------------------------------------------------------
    // EDIT
    // ---------------------------------------------------------------
    const openEdit = (t) => {
        setEditingType(t);
        setEditForm({
            name: t.name || "",
            consultation_fee:
                t.consultation_fee !== null && t.consultation_fee !== undefined
                    ? formatMoney(String(t.consultation_fee))
                    : "",
            service_charge:
                t.service_charge !== null && t.service_charge !== undefined
                    ? formatMoney(String(t.service_charge))
                    : "",
        });
        setEditError("");
        setIsEditOpen(true);
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;

        const moneyFields = ["consultation_fee", "service_charge"];
        const nextValue = moneyFields.includes(name)
            ? formatMoney(value)
            : value;

        setEditForm((prev) => ({ ...prev, [name]: nextValue }));
        setEditError("");
    };

    const handleSaveEdit = async () => {
        if (!editingType) return;

        const id = editingType.id ?? editingType.consultation_type_id;
        if (!id) {
            setEditError("Missing consultation type ID.");
            return;
        }

        // --- Validation ---
        const errs = [];

        if (!editForm.name || !editForm.name.trim()) {
            errs.push("Name is required.");
        } else if (editForm.name.length > 1000) {
            errs.push("Name must be 1000 characters or less.");
        }

        const cleanFee = unformatMoney(editForm.consultation_fee);
        if (cleanFee === "" || cleanFee === null) {
            errs.push("Consultation fee is required.");
        } else {
            const fee = parseFloat(cleanFee);
            if (isNaN(fee) || fee < 0.01) {
                errs.push("Consultation fee must be at least 0.01.");
            }
        }

        const cleanCharge = unformatMoney(editForm.service_charge);
        if (cleanCharge !== "" && cleanCharge !== null) {
            const charge = parseFloat(cleanCharge);
            if (isNaN(charge) || charge < 0.01) {
                errs.push("Service charge must be at least 0.01 (or leave it empty).");
            }
        }

        if (errs.length > 0) {
            setEditError(errs.join(" "));
            return;
        }

        setIsSaving(true);
        setEditError("");

        try {
            const companyId = parseInt(localStorage.getItem("companyId") || "1");

            const payload = {
                id: parseInt(id),
                company_id: companyId,
                name: editForm.name.trim(),
                consultation_fee: parseFloat(cleanFee),
                service_charge:
                    cleanCharge !== "" ? parseFloat(cleanCharge) : null,
            };

            console.log("Updating consultation type:", payload);

            await axios.post(
                `${BASE_URL}/api/consultation_type/update`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            Swal.fire({
                icon: "success",
                title: "Updated!",
                text: "Consultation type updated successfully.",
                timer: 1400,
                showConfirmButton: false,
                timerProgressBar: true,
            });

            setIsEditOpen(false);
            fetchTypes();
        } catch (err) {
            console.error("Error updating consultation type:", err);
            let msg = "Failed to update consultation type.";

            if (err.response?.data?.errors) {
                const apiErrors = err.response.data.errors;
                const firstKey = Object.keys(apiErrors)[0];
                msg = Array.isArray(apiErrors[firstKey])
                    ? apiErrors[firstKey][0]
                    : "Validation failed.";
            } else if (err.response?.data?.message) {
                const m = err.response.data.message;
                if (typeof m === "object") {
                    const firstKey = Object.keys(m)[0];
                    msg = Array.isArray(m[firstKey])
                        ? m[firstKey][0]
                        : JSON.stringify(m);
                } else {
                    msg = m;
                }
            } else if (err.request) {
                msg = "Unable to connect to server.";
            }

            setEditError(msg);
        } finally {
            setIsSaving(false);
        }
    };

    // ---------------------------------------------------------------
    // DELETE
    // ---------------------------------------------------------------
    const openDelete = (t) => {
        setDeleteTarget(t);
        setIsDeleteOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        const id = deleteTarget.id ?? deleteTarget.consultation_type_id;
        if (!id) return;

        setIsDeleting(true);
        try {
            await axios.delete(
                `${BASE_URL}/api/consultation_type/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: "Consultation type deleted.",
                timer: 1400,
                showConfirmButton: false,
                timerProgressBar: true,
            });

            setIsDeleteOpen(false);
            fetchTypes();
        } catch (err) {
            let msg = "Failed to delete.";
            if (err.response?.data?.message) {
                msg =
                    typeof err.response.data.message === "string"
                        ? err.response.data.message
                        : "Failed to delete.";
            }
            Swal.fire({
                icon: "error",
                title: "Error",
                text: msg,
                confirmButtonColor: "#2F80ED",
            });
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className={classes.page}>
            <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
            <div className={classes.main}>
                <Header toggleSidebar={toggleSidebar} />
                <div className={classes.content}>
                    <div className={classes.topSection}>
                        <div>
                            <h2 className={classes.pageTitle}>Consultation Types</h2>
                            <p className={classes.breadcrumb}>
                                Configuration <span>&gt;</span> Consultation Types
                            </p>
                        </div>
                        <button
                            className={classes.newBtn}
                            onClick={() => setIsCreateOpen(true)}
                        >
                            <FaPlus />
                            <span>Create Consultation Type</span>
                        </button>
                    </div>

                    <div className={classes.toolbar}>
                        <div className={classes.searchBox}>
                            <FaSearch />
                            <input
                                type="text"
                                placeholder="Search consultation types..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <button className={classes.resetBtn} onClick={fetchTypes}>
                            <FaRedoAlt /> Refresh
                        </button>
                    </div>

                    {error && (
                        <div className={classes.errorMessage}>
                            <FaExclamationTriangle /> {error}
                        </div>
                    )}

                    <div className={classes.tableCard}>
                        {loading ? (
                            <div className={classes.loadingState}>
                                <FaSpinner className={classes.spin} />
                                <span>Loading...</span>
                            </div>
                        ) : filteredTypes.length === 0 ? (
                            <div className={classes.emptyState}>
                                <FaStethoscope />
                                <h4>No consultation types yet</h4>
                                <p>Click "Create Consultation Type" to add one.</p>
                            </div>
                        ) : (
                            <table className={classes.table}>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Name</th>
                                        <th className={classes.moneyHeader}>Consultation Fee (₦)</th>
                                        <th className={classes.moneyHeader}>Service Charge (₦)</th>
                                        <th className={classes.moneyHeader}>Total (₦)</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTypes.map((t, i) => {
                                        const fee = parseFloat(t.consultation_fee || 0);
                                        const charge = parseFloat(t.service_charge || 0);
                                        const total = fee + charge;

                                        return (
                                            <tr key={t.id ?? i}>
                                                <td>{i + 1}</td>
                                                <td>
                                                    <div className={classes.nameCell}>
                                                        <span className={classes.iconBadge}>
                                                            <FaStethoscope />
                                                        </span>
                                                        <strong>{t.name || "—"}</strong>
                                                    </div>
                                                </td>
                                                <td className={classes.money}>
                                                    ₦{fee.toLocaleString()}
                                                </td>
                                                <td className={classes.money}>
                                                    {t.service_charge === null ||
                                                    t.service_charge === undefined
                                                        ? "—"
                                                        : `₦${charge.toLocaleString()}`}
                                                </td>
                                                <td className={`${classes.money} ${classes.moneyStrong}`}>
                                                    ₦{total.toLocaleString()}
                                                </td>
                                                <td>
                                                    <div className={classes.actions}>
                                                        <button
                                                            className={classes.editBtn}
                                                            onClick={() => openEdit(t)}
                                                            title="Edit"
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button
                                                            className={classes.deleteBtn}
                                                            onClick={() => openDelete(t)}
                                                            title="Delete"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* CREATE MODAL */}
            <CreateConsultationTypeModal
                isOpen={isCreateOpen}
                onClose={() => setIsCreateOpen(false)}
                onSaved={handleCreateSaved}
            />

            {/* EDIT MODAL */}
            {isEditOpen && editingType && (
                <div
                    className={classes.modalOverlay}
                    onClick={() => !isSaving && setIsEditOpen(false)}
                >
                    <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.modalHeader}>
                            <div className={classes.modalHeaderLeft}>
                                <div className={classes.modalHeaderIcon}>
                                    <FaStethoscope />
                                </div>
                                <div>
                                    <h3>Edit Consultation Type</h3>
                                    <p>Update the details below</p>
                                </div>
                            </div>
                            <button
                                className={classes.modalCloseBtn}
                                onClick={() => setIsEditOpen(false)}
                                disabled={isSaving}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className={classes.modalBody}>
                            {editError && (
                                <div className={classes.modalError}>
                                    <FaExclamationTriangle /> {editError}
                                </div>
                            )}

                            <div className={classes.field}>
                                <label>
                                    Consultation Name <span className={classes.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={editForm.name}
                                    onChange={handleEditChange}
                                    disabled={isSaving}
                                    maxLength={1000}
                                />
                                <span className={classes.fieldHint}>
                                    Max 1000 characters, must be unique
                                </span>
                            </div>

                            <div className={classes.row}>
                                <div className={classes.field}>
                                    <label>
                                        Consultation Fee <span className={classes.required}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        name="consultation_fee"
                                        value={editForm.consultation_fee}
                                        onChange={handleEditChange}
                                        disabled={isSaving}
                                    />
                                    <span className={classes.fieldHint}>
                                        Minimum 0.01
                                    </span>
                                </div>
                                <div className={classes.field}>
                                    <label>Service Charge</label>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        name="service_charge"
                                        value={editForm.service_charge}
                                        onChange={handleEditChange}
                                        disabled={isSaving}
                                    />
                                    <span className={classes.fieldHint}>
                                        Optional — min 0.01 if provided
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className={classes.modalFooter}>
                            <button
                                className={classes.cancelBtn}
                                onClick={() => setIsEditOpen(false)}
                                disabled={isSaving}
                            >
                                Cancel
                            </button>
                            <button
                                className={classes.saveBtn}
                                onClick={handleSaveEdit}
                                disabled={isSaving}
                            >
                                {isSaving ? (
                                    <>
                                        <FaSpinner className={classes.spin} /> Saving...
                                    </>
                                ) : (
                                    <>
                                        <FaSave /> Save Changes
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* DELETE MODAL */}
            {isDeleteOpen && deleteTarget && (
                <div
                    className={classes.modalOverlay}
                    onClick={() => !isDeleting && setIsDeleteOpen(false)}
                >
                    <div className={classes.modalSmall} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.deleteHeader}>
                            <div className={classes.deleteIconWrapper}>
                                <FaExclamationTriangle />
                            </div>
                            <h3>Delete Consultation Type?</h3>
                            <p>
                                You're about to permanently delete this consultation
                                type. This action cannot be undone.
                            </p>
                        </div>

                        <div className={classes.deleteBody}>
                            <div className={classes.deletePreview}>
                                <div className={classes.deletePreviewIcon}>
                                    <FaStethoscope />
                                </div>
                                <div>
                                    <h4>{deleteTarget.name}</h4>
                                    <p>
                                        Fee: ₦
                                        {parseFloat(
                                            deleteTarget.consultation_fee || 0
                                        ).toLocaleString()}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className={classes.deleteFooter}>
                            <button
                                className={classes.cancelBtn}
                                onClick={() => setIsDeleteOpen(false)}
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                className={classes.dangerBtn}
                                onClick={handleConfirmDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <>
                                        <FaSpinner className={classes.spin} /> Deleting...
                                    </>
                                ) : (
                                    <>
                                        <FaTrash /> Yes, Delete
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConsultationType;